/* AdCarousel — Framer Motion drag spring, weighted ad rotation.
   Each ad: { id, advertiserName, creativeUrl, destinationUrl, rotationWeight } */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

const isVideoUrl = (url) => {
  if (!url) return false;
  if (url.startsWith("data:video/")) return true;
  return /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i.test(url);
};

/* Durée d'affichage dégressive : la première campagne du trajet reste 8 s à
   l'écran, chaque suivante 2 s de moins. L'attention du passager décroît au fil
   de la course, la durée suit.

   La décroissance est PLANCHONNÉE : appliquée à la lettre, la 5ᵉ campagne
   tomberait à 0 s puis passerait en négatif, et le carrousel défilerait en
   boucle instantanée. Sous MIN_SLIDE_MS, la durée ne bouge plus.

   Le compte porte sur les campagnes RÉELLEMENT AFFICHÉES depuis le début du
   scan, pas sur la position dans la rotation : au second tour, la première
   campagne ne repart donc pas à 8 s. */
const FIRST_SLIDE_MS = 8000;
const SLIDE_STEP_MS = 2000;
const MIN_SLIDE_MS = 2000;

export function slideDurationMs(shownCount) {
  const n = Math.max(0, Number(shownCount) || 0);
  return Math.max(MIN_SLIDE_MS, FIRST_SLIDE_MS - n * SLIDE_STEP_MS);
}

function buildWeighted(campaigns) {
  // CAROUSEL campaigns become one slide per media; others expand by weight
  let groups = campaigns.map((c) => {
    if (c.mediaType === "CAROUSEL" && Array.isArray(c.mediaUrls) && c.mediaUrls.length) {
      return c.mediaUrls.map((url, i) => ({ ...c, creativeUrl: url, slideKey: `${c.id}-m${i}` }));
    }
    const w = Math.max(1, Number(c.rotationWeight) || 1);
    return Array.from({ length: w }, (_, i) => ({ ...c, slideKey: `${c.id}-w${i}` }));
  });

  // House ad only fills the gap when no real campaign is available
  const real = groups.filter((g) => !g[0]?.isHouseAd);
  if (real.length) groups = real;

  /* Interleave campaigns instead of stacking each one's weighted copies back to
     back: with weights like 5/3/2, stacking meant 25s of the same ad before the
     next campaign ever appeared. Greedy pick: campaign with the most slides
     left, avoiding the same campaign twice in a row when possible. */
  const remaining = groups.map((g) => [...g]);
  const slides = [];
  let lastId = null;
  while (remaining.some((g) => g.length)) {
    const candidates = remaining
      .filter((g) => g.length)
      .sort((a, b) => b.length - a.length);
    const pick = candidates.find((g) => g[0].id !== lastId) || candidates[0];
    const slide = pick.shift();
    slides.push(slide);
    lastId = slide.id;
  }
  return slides.length ? slides : campaigns;
}

const CTA_LABELS = {
  VISIT: "En savoir +",
  DOWNLOAD: "Télécharger",
  CALL: "Appeler",
  WHATSAPP: "WhatsApp",
};

function ctaHref(c) {
  const v = c.ctaValue || c.destinationUrl || "";
  if (!v) return null;
  if (c.ctaType === "CALL") return `tel:${v.replace(/[^+\d]/g, "")}`;
  if (c.ctaType === "WHATSAPP") return `https://wa.me/${v.replace(/\D/g, "")}`;
  return v;
}

export default function AdCarousel({ campaigns, scanEventId }) {
  const rotation = useMemo(() => buildWeighted(campaigns || []), [campaigns]);
  const [idx, setIdx] = useState(0);
  // Campagnes déjà affichées depuis le début du scan. C'est ce compteur qui
  // pilote la dégressivité — `idx` reboucle et ne peut donc pas la porter.
  const [shown, setShown] = useState(0);
  const impressedRef = useRef(new Set());
  const containerRef = useRef(null);

  /* Point de passage unique de toute navigation, automatique comme au doigt.
     Faire avancer `shown` à chaque changement remplit deux rôles : la durée
     décroît, et la minuterie repart de zéro. Sans ce second effet, un balayage
     manuel laisserait courir le compte à rebours en cours et la campagne
     choisie disparaîtrait presque aussitôt. */
  const goTo = useCallback((next) => {
    setIdx((i) => (typeof next === "function" ? next(i) : next));
    setShown((n) => n + 1);
  }, []);

  /* Rotation à durée variable : un setInterval impose un pas fixe, on enchaîne
     donc un setTimeout par campagne. */
  useEffect(() => {
    if (rotation.length <= 1) return;
    const t = setTimeout(
      () => goTo((i) => (i + 1) % rotation.length),
      slideDurationMs(shown)
    );
    return () => clearTimeout(t);
  }, [shown, rotation.length, goTo]);

  /* Les créations sont servies par URL (et non plus incluses dans le JSON du
     scan) : la suivante n'est donc pas encore en mémoire au moment où le
     carrousel tourne. On la télécharge pendant que la courante est à l'écran,
     sinon la rotation laisserait un cadre vide le temps du chargement. */
  useEffect(() => {
    if (rotation.length <= 1) return;
    const next = rotation[(idx + 1) % rotation.length];
    const url = next?.creativeUrl;
    if (!url || isVideoUrl(url)) return;
    const img = new Image();
    img.src = url;
  }, [idx, rotation]);

  const impressionIdsRef = useRef({}); // campaignId -> backend impressionId
  const viewRef = useRef({ campaignId: null, startedAt: 0 });

  const flushViewDuration = (nextCampaignId) => {
    const { campaignId, startedAt } = viewRef.current;
    if (campaignId && campaignId !== nextCampaignId && startedAt) {
      const impId = impressionIdsRef.current[campaignId];
      const durationMs = Date.now() - startedAt;
      if (impId && durationMs > 300) {
        api
          .post(`/events/impression/${impId}/duration`, { durationMs })
          .catch(() => {});
      }
    }
    viewRef.current = { campaignId: nextCampaignId, startedAt: Date.now() };
  };

  // Log impressions (one per campaignId per session) + track viewing time
  useEffect(() => {
    if (!rotation.length || !scanEventId) return;
    const c = rotation[idx];
    if (!c) return;
    flushViewDuration(c.isHouseAd ? null : c.id);
    if (c.isHouseAd || impressedRef.current.has(c.id)) return;
    impressedRef.current.add(c.id);
    api
      .post("/events/impression", { campaignId: c.id, scanEventId })
      .then(({ data }) => {
        if (data?.impressionId) impressionIdsRef.current[c.id] = data.impressionId;
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, rotation, scanEventId]);

  // Flush the last ad's watch time when leaving the page
  useEffect(() => () => flushViewDuration(null), []);

  if (!rotation.length) return null;
  const current = rotation[idx];

  const handleClick = async () => {
    const href = ctaHref(current);
    if (!current || current.isHouseAd) {
      if (href) window.open(href, "_blank", "noopener");
      return;
    }
    try {
      const { data } = await api.post("/events/click", {
        campaignId: current.id,
        scanEventId,
      });
      const dest =
        current.ctaType && current.ctaType !== "VISIT"
          ? href
          : data?.destinationUrl || href;
      if (dest) window.open(dest, "_blank", "noopener");
    } catch (e) {
      if (href) window.open(href, "_blank", "noopener");
    }
  };

  return (
    <div className="w-full" data-testid="advertiser-carousel">
      <motion.div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-3xl bg-secondary text-secondary-foreground"
        style={{ height: 260, boxShadow: "var(--shadow-card)" }}
        drag="x"
        dragElastic={0.15}
        dragConstraints={{ left: 0, right: 0 }}
        onDragEnd={(_, info) => {
          if (info.offset.x < -60) goTo((i) => (i + 1) % rotation.length);
          else if (info.offset.x > 60)
            goTo((i) => (i - 1 + rotation.length) % rotation.length);
        }}
      >
        <AnimatePresence mode="wait">
          <motion.button
            key={(current.slideKey || current.id) + "-" + idx}
            type="button"
            onClick={handleClick}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            className="absolute inset-0 w-full h-full text-left"
          >
            {current.creativeUrl ? (
              isVideoUrl(current.creativeUrl) || current.creativeType === "video" ? (
                <video
                  key={current.creativeUrl}
                  src={current.creativeUrl}
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  data-testid="ad-video"
                />
              ) : (
                <img
                  src={current.creativeUrl}
                  alt={current.advertiserName}
                  className="w-full h-full object-cover"
                  draggable={false}
                  decoding="async"
                  fetchpriority="high"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center stripes bg-secondary text-secondary-foreground">
                <div className="text-center px-6">
                  <div className="text-xs uppercase tracking-[0.2em] opacity-70">
                    GoCab Boost
                  </div>
                  <div className="font-heading text-3xl font-bold mt-2">
                    Vous voyez ça ?
                  </div>
                  <div className="text-sm opacity-80 mt-2 max-w-[260px] mx-auto">
                    Votre marque pourrait être ici. Devenez annonceur.
                  </div>
                </div>
              </div>
            )}
            {/* Overlay */}
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 via-black/20 to-transparent text-white">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">
                    Annonceur
                  </div>
                  <div className="font-heading text-xl font-semibold leading-tight">
                    {current.advertiserName}
                  </div>
                </div>
                <span
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{
                    background: "var(--color-brand)",
                    color: "#fff",
                  }}
                >
                  {CTA_LABELS[current.ctaType] || CTA_LABELS.VISIT}
                </span>
              </div>
            </div>
          </motion.button>
        </AnimatePresence>
      </motion.div>

      {/* Dots (unique campaigns, capped) */}
      {(() => {
        const uniques = [];
        const seen = new Set();
        rotation.forEach((c) => {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            uniques.push(c);
          }
        });
        const activeId = rotation[idx]?.id;
        return (
          <div className="mt-3 flex items-center justify-center gap-1.5" role="tablist">
            {uniques.slice(0, 8).map((c, i) => (
              <button
                key={c.id}
                aria-label={`Annonce ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
                onClick={() => goTo(rotation.findIndex((r) => r.id === c.id))}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: c.id === activeId ? 22 : 6,
                  background:
                    c.id === activeId
                      ? "var(--color-brand)"
                      : "hsl(var(--muted-foreground) / 0.4)",
                }}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}
